import React, { useCallback, useState, useRef, useEffect } from 'react';
import { Button, Input, message, Space, Row, Col } from 'antd';
import { EditOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSensitiveCheck } from '../hooks/useSensitiveCheck';
import { findSensitiveWords } from '../utils/sensitiveWords';

const { TextArea } = Input;

interface OrderNoteEditorProps {
  value?: string;
  onSave?: (note: string) => Promise<void>; // 可选，在只读模式下可以为 undefined
  maxLength?: number;
  loading?: boolean;
  readOnly?: boolean; // 只读模式，隐藏编辑功能
}

const OrderNoteEditor: React.FC<OrderNoteEditorProps> = ({
  value = '',
  onSave,
  maxLength = 300,
  loading = false,
  readOnly = false,
}) => {
  const { t } = useTranslation();
  const [editing, setEditing] = useState(false);
  const textAreaRef = useRef<any>(null);

  // 使用自定义 Hook（只实时检查敏感词，不检查语言）
  const {
    text,
    setText,
    errorWords,
    handleChange: originalHandleChange,
  } = useSensitiveCheck({
    initialText: value,
    maxResults: 3,
    loadFromConfig: true, // 从配置文件加载敏感词
    realtimeCheck: true, // 实时检查敏感词
  });

  // 包装 handleChange，添加自动滚动到底部的逻辑
  const handleChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    originalHandleChange(e);
    
    // 延迟执行，确保 DOM 已经更新
    setTimeout(() => {
      if (textAreaRef.current?.resizableTextArea?.textArea) {
        const textArea = textAreaRef.current.resizableTextArea.textArea;
        // 检查是否出现滚动条
        if (textArea.scrollHeight > textArea.clientHeight) {
          // 滚动到底部，确保最后一行文本可见（不遮挡字符计数）
          textArea.scrollTop = textArea.scrollHeight - textArea.clientHeight;
        }
      }
    }, 0);
  }, [originalHandleChange]);

  // 当文本变化时，检查是否需要滚动到底部
  useEffect(() => {
    if (editing && textAreaRef.current?.resizableTextArea?.textArea) {
      const textArea = textAreaRef.current.resizableTextArea.textArea;
      // 检查是否出现滚动条
      if (textArea.scrollHeight > textArea.clientHeight) {
        // 滚动到底部，确保最后一行文本可见
        textArea.scrollTop = textArea.scrollHeight - textArea.clientHeight;
      }
    }
  }, [text, editing]);

  // 开始编辑
  const handleEdit = useCallback(() => {
    setText(value); // 重置为原始值
    setEditing(true);
  }, [value, setText]);

  // 取消编辑
  const handleCancel = useCallback(() => {
    setText(value); // 恢复原始值
    setEditing(false);
  }, [value, setText]);

  // 提交备注
  const handleSubmit = useCallback(async () => {
    if (!onSave) {
      return;
    }

    if (text.trim().length === 0) {
      message.warning(t('central.parcels.messages.noteCannotBeEmpty'));
      return;
    }

    // 提交时检查敏感词
    const foundWords = findSensitiveWords(text, 3);
    if (foundWords.length > 0) {
      message.error(
        t('central.parcels.messages.noteContainsSensitiveWords', {
          words: foundWords.slice(0, 3).join('、'),
        })
      );
      return;
    }

    try {
      await onSave(text.trim());
      setEditing(false);
      message.success(t('central.parcels.messages.noteSavedSuccessfully'));
    } catch (error) {
      console.error('保存备注失败:', error);
      message.error(t('central.parcels.messages.noteSaveFailed'));
    }
  }, [text, onSave, t]);

  return (
    <div className="mb-4 relative">
      {/* 右上角编辑图标 */}
      {!readOnly && (
        <div style={{ position: 'absolute', right: 0, top: 0, zIndex: 1 }}>
          {!editing && (
            <Button 
              icon={<EditOutlined />} 
              size="small" 
              onClick={handleEdit} 
              type="text"
            />
          )}
        </div>
      )}

      {editing ? (
        <>
          {/* 编辑模式：显示标签和输入框 */}
          <Row className="mb-3">
            <Col span={9} className="text-gray-500">
              {t('central.parcels.pageDetails.note')}:
            </Col>
            <Col span={1} className="text-gray-500"></Col>
            <Col span={14}>
              <div 
                style={{ 
                  position: 'relative',
                  border: `1px solid ${errorWords.length > 0 ? '#ff4d4f' : '#d9d9d9'}`,
                  borderRadius: '6px',
                  overflow: 'hidden',
                }}
              >
                <TextArea
                  ref={textAreaRef}
                  value={text}
                  onChange={handleChange}
                  maxLength={maxLength}
                  autoSize={{ minRows: 4, maxRows: 10 }} // 自动扩展高度，最小4行，最大10行
                  style={{
                    border: 'none',
                    paddingBottom: '24px', // 为字符计数留出一行空间
                    boxShadow: 'none',
                    resize: 'none', // 禁止拖拽调整大小
                    width: '100%',
                  }}
                  placeholder={t('central.parcels.placeholders.enterNote') || undefined}
                />
                {/* 字符计数 - 始终显示在文本框内部右下角，占据一行 */}
                <div
                  style={{
                    position: 'absolute',
                    bottom: '4px', // 紧贴文本框底部（边框内侧）
                    right: '8px', // 距离文本框右侧
                    fontSize: '12px',
                    color: '#999',
                    pointerEvents: 'none',
                    padding: '4px 8px',
                    borderRadius: '2px',
                    lineHeight: '16px',
                    height: '24px', // 固定高度占一行
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    zIndex: 10, // 确保在文本框内容之上
                    userSelect: 'none', // 防止选中
                    boxSizing: 'border-box',
                  }}
                >
                  {text.length}/{maxLength}
                </div>
              </div>
              {/* 敏感词错误提示 */}
              {errorWords.length > 0 && (
                <div className="text-xs text-red-500 mt-1">
                  {t('central.parcels.messages.containsSensitiveWords')}:{' '}
                  <span className="font-bold">
                    {errorWords.slice(0, 3).join('、')}
                  </span>
                </div>
              )}
            </Col>
          </Row>
          {/* 提交和取消按钮 - 放在输入框下方，与文本框拉开间距 */}
          <Row>
            <Col span={10}></Col>
            <Col span={14}>
              <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'flex-end' }}>
                <Space>
                  <Button 
                    icon={<CheckOutlined />} 
                    size="small" 
                    onClick={handleSubmit}
                    loading={loading}
                    disabled={errorWords.length > 0 || text.trim().length === 0}
                  />
                  <Button 
                    icon={<CloseOutlined />} 
                    size="small" 
                    onClick={handleCancel}
                    disabled={loading}
                  />
                </Space>
              </div>
            </Col>
          </Row>
        </>
      ) : (
        /* 显示模式：显示标签和内容 - 上下布局 */
        <div className="mb-3">
          <div className="text-gray-500 text-sm mb-1">{t('central.parcels.pageDetails.note')}:</div>
          <div className="break-words" style={{ paddingRight: '32px' }}>{value || '-'}</div>
        </div>
      )}
    </div>
  );
};

export default OrderNoteEditor;

